<template>
    <v-app>
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                                Search Form
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-form @submit.prevent="loadReport">
                                    <v-row class="px-2 pt-4 pb-0">
                                        <v-col cols="12" lg="2" md="2">
                                            <v-autocomplete
                                                v-model="buyer_id.value.value"
                                                :items="getCustomerMapList"
                                                :item-title="item => `${item.customer?.company_name ?? ''} - ${item.customer?.code ?? ''}`"
                                                :item-value="item => item.customer?.id ?? null"
                                                variant="outlined"
                                                density="compact"
                                                :error-messages="buyer_id.errorMessage.value"
                                                clearable
                                            >
                                            <template #label>
                                                Buyer <span class="text-red">*</span>
                                            </template>
                                            </v-autocomplete>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-date-input input-format="dd/mm/yyyy" label="From Date" density="compact" prepend-icon=""
                                                prepend-inner-icon="mdi-calendar-month" variant="outlined"
                                                bg-color="grey-lighten-4" v-model="from_date.value.value"
                                                :error-messages="from_date.errorMessage.value" autocomplete="off"
                                                clearable></v-date-input>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-date-input input-format="dd/mm/yyyy" label="To Date" density="compact" prepend-icon=""
                                                prepend-inner-icon="mdi-calendar-month" variant="outlined"
                                                bg-color="grey-lighten-4" v-model="to_date.value.value"
                                                :error-messages="to_date.errorMessage.value" autocomplete="off"
                                                clearable></v-date-input>
                                        </v-col>

                                        <v-col lg="2" md="2" cols="12">
                                            <v-btn class="bg-brand w-15" type="submit" ref="searchButton"
                                                :loading="loading">
                                                Search
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-form>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>

        <!-- Report Table -->
        <v-container fluid style="margin-top: -20px">
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="reportPanel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header"
                                expand-icon="mdi-chevron-down">
                                <v-icon class="me-2">mdi-table</v-icon>
                                Textile Order Report Data
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field v-model="search" append-inner-icon="mdi-magnify" label="Search"
                                            single-line hide-details density="compact" variant="outlined"
                                            bg-color="grey-lighten-4" class="pl-2" clearable></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="success"
                                                    @click="exportToCSV">
                                                    CSV
                                                </v-btn>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="primary"
                                                    @click="exportToExcel">
                                                    EXCEL
                                                </v-btn>
                                                <v-btn prepend-icon="mdi-download" variant="flat" color="red"
                                                    @click="exportToPDF">
                                                    PDF
                                                </v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-row class="pt-0">
                                    <v-col cols="12">
                                        <div class="scrollable-table-wrapper">
                                            <v-data-table :headers="headers" :items="reportData" :loading="loading"
                                                v-model:items-per-page="itemsPerPage" :search="search" :hover="true"
                                                density="compact" class="responsive-table">
                                                <template v-slot:item.index="{ index }">
                                                    {{ ++index }}
                                                </template>

                                                <template v-slot:item.order_no_and_fabric_type="{ item }">
                                                    {{ item?.order?.code +' - '+item?.style?.fabric_type }}
                                                </template>

                                                <template v-slot:item.balance="{ item }">
                                                    {{ item.yarn_delivery * item.rec_fabric_knit }}
                                                </template>

                                                <template v-slot:no-data>
                                                    <div class="text-center py-4">
                                                        <p>No finishing output data found</p>
                                                    </div>
                                                </template>

                                            </v-data-table>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>

    </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import Axios from "axios";
import { prefix } from "@/store/api";
import formattedDate from "@/plugins/dateFormatter";

export default {
    name: "FinishingOutputReport",

    data() {
        return {
            panel: [0],
            reportPanel: [0],
            loading: false,
            search: "",
            itemsPerPage: 10,
            reportData: [],
            headers: [
                {
                    title: "Buyer Name",
                    align: "center",
                    key: "order.buyer_order.buyer.name",
                    sortable: false,
                },
                {
                    title: "Order No & Fabric Type",
                    key: "order_no_and_fabric_type",
                    align: "left",
                    sortable: true,
                },
                {
                    title: "Yarn Count",
                    key: "yarn_count",
                    align: "left",
                    sortable: false,
                },
                {
                    title: "Color",
                    key: "color.name",
                    align: "left",
                    sortable: false,
                },
                {
                    title: "Dyeing Name",
                    key: "order.dyeing_house.name",
                    align: "left",
                    sortable: false,
                },
                {
                    title: "Qty In Pcs",
                    key: "buyer_order_item.order_quantity",
                    align: "center",
                    sortable: true,
                },
                {
                    title: "Re-Qty-In KGs",
                    key: "req_qty_kg",
                    align: "center",
                    sortable: true,
                },
                {
                    title: "Yarn Dely",
                    key: "yarn_delivery",
                    align: "center",
                    sortable: true,
                },
                 {
                    title: "Rcvd Fabric Kniting",
                    key: "rec_fabric_knit",
                    align: "center",
                    sortable: true,
                },
               {
                    title: "Dyeing Delivery",
                    key: "dye_delivery",
                    align: "center",
                    sortable: true,
                },
                {
                    title: "Dyeing Receive",
                    key: "dye_receive",
                    align: "center",
                    sortable: true,
                },
                {
                    title: "Receive Yarn",
                    key: "yarn_receive",
                    align: "center",
                    sortable: true,
                },
                {
                    title: "Balance",
                    key: "balance",
                    align: "center",
                    sortable: true,
                },
            ],
        };
    },

    setup() {
        const store = useStore();

        const { handleSubmit } = useForm({
            validationSchema: yup.object({
                buyer_id: yup.number().nullable(),
                from_date: yup.date().nullable(),
                to_date: yup.date().nullable(),
            }),
        });

        const buyer_id = useField("buyer_id");
        const from_date = useField("from_date");
        const to_date = useField("to_date");

        return {
            buyer_id,
            from_date,
            to_date,
        };
    },

    computed: {
        styleList() {
            return this.$store.getters.getProductionStyleList || [];
        },
        getCustomerMapList() {
            return this.$store.getters.getCustomerMapList;
        },
    },

    methods: {
        loadReport() {
            this.loading = true;
            const params = {};

            if (this.buyer_id.value.value) {
                params.buyer_id = this.buyer_id.value.value;
            }

            if (this.from_date.value.value) {
                params.from_date = formattedDate(this.from_date.value.value);
            }

            if (this.to_date.value.value) {
                params.to_date = formattedDate(this.to_date.value.value);
            }

            Axios.get(`${prefix}bhp/reports/textile/order`, { params })
                .then((response) => {
                    this.reportData = response.data.data || [];
                    if (this.reportData.length > 0) {
                        toast.success("Report loaded successfully!");
                    } else {
                        toast.info("No data found for the selected criteria");
                    }
                })
                .catch((err) => {
                    toast.error(err.response?.data?.message || "Failed to load report");
                    this.reportData = [];
                })
                .finally(() => {
                    this.loading = false;
                });
            },
        exportToCSV() {
            if (!this.reportData.length) {
                toast.info("No data to export");
                return;
            }

            let data = [
                [
                    "#SL",
                    "Buyer Name",
                    "Order No & Fabric Type",
                    "Yarn Count",
                    "Color",
                    "Dyeing Name",
                    "Qty In Pcs",
                    "Re-Qty-In KGs",
                    "Yarn Delivery",
                    "Rcvd Fabric Knitting",
                    "Dyeing Delivery",
                    "Dyeing Receive",
                    "Receive Yarn",
                    "Balance",
                ],
            ];

            this.reportData.forEach((item, index) => {
                data.push([
                    index + 1,
                    item?.order?.buyer_order?.buyer?.name || "N/A",
                    `${item?.order?.code || ""} - ${item?.style?.fabric_type || ""}`,
                    item?.yarn_count || "N/A",
                    item?.color?.name || "N/A",
                    item?.order?.dyeing_house?.name || "N/A",
                    item?.buyer_order_item?.order_quantity || 0,
                    item?.req_qty_kg || 0,
                    item?.yarn_delivery || 0,
                    item?.rec_fabric_knit || 0,
                    item?.dye_delivery || 0,
                    item?.dye_receive || 0,
                    item?.yarn_receive || 0,
                    item.yarn_delivery * item.rec_fabric_knit,
                ]);
            });

            this.$exportTableToCSV(
                data,
                "textile_order_report_" + this.$fileDate()
            );
        },

        exportToExcel() {
            if (!this.reportData.length) {
                toast.info("No data to export");
                return;
            }

            let data = [
                [
                    "#SL",
                    "Buyer Name",
                    "Order No & Fabric Type",
                    "Yarn Count",
                    "Color",
                    "Dyeing Name",
                    "Qty In Pcs",
                    "Re-Qty-In KGs",
                    "Yarn Delivery",
                    "Rcvd Fabric Knitting",
                    "Dyeing Delivery",
                    "Dyeing Receive",
                    "Receive Yarn",
                    "Balance",
                ],
            ];

            this.reportData.forEach((item, index) => {
                data.push([
                    index + 1,
                    item?.order?.buyer_order?.buyer?.name || "N/A",
                    `${item?.order?.code || ""} - ${item?.style?.fabric_type || ""}`,
                    item?.yarn_count || "N/A",
                    item?.color?.name || "N/A",
                    item?.order?.dyeing_house?.name || "N/A",
                    item?.buyer_order_item?.order_quantity || 0,
                    item?.req_qty_kg || 0,
                    item?.yarn_delivery || 0,
                    item?.rec_fabric_knit || 0,
                    item?.dye_delivery || 0,
                    item?.dye_receive || 0,
                    item?.yarn_receive || 0,
                     item.yarn_delivery * item.rec_fabric_knit,
                ]);
            });

            this.$exportTableToExcel(
                data,
                "Textile Order Report",
                "textile_order_report_" + this.$fileDate()
            );
        },

        exportToPDF() {
            if (!this.reportData.length) {
                toast.info("No data to export");
                return;
            }

            let serialNumber = 1;

            const columns = [
                "#SL",
                "Buyer Name",
                "Order No & Fabric Type",
                "Yarn Count",
                "Color",
                "Dyeing Name",
                "Qty In Pcs",
                "Re-Qty-In KGs",
                "Yarn Delivery",
                "Rcvd Fabric Knitting",
                "Dyeing Delivery",
                "Dyeing Receive",
                "Receive Yarn",
                "Balance",
            ];

            const rows = this.reportData.map(item => [
                serialNumber++,
                item?.order?.buyer_order?.buyer?.name || "N/A",
                `${item?.order?.code || ""} - ${item?.style?.fabric_type || ""}`,
                item?.yarn_count || "N/A",
                item?.color?.name || "N/A",
                item?.order?.dyeing_house?.name || "N/A",
                item?.buyer_order_item?.order_quantity || 0,
                item?.req_qty_kg || 0,
                item?.yarn_delivery || 0,
                item?.rec_fabric_knit || 0,
                item?.dye_delivery || 0,
                item?.dye_receive || 0,
                item?.yarn_receive || 0,
                item.yarn_delivery * item.rec_fabric_knit,
            ]);

            this.$exportTableToPDF(
                columns,
                rows,
                "Textile Order Report",
                "textile_order_report_" + this.$fileDate(),
                "landscape"
            );
        },

    },

    created() {
        // Load required data
        if (this.$store.getters.getProductionStyleList.length === 0) {
            this.$store.dispatch("getProductionStyleList");
        }
        this.loading = true;
        this.$store.dispatch('getCustomerMapList').then((response) => {
            this.loading = false;
        });
    },
};
</script>

<style scoped>
.scrollable-table-wrapper {
    overflow-x: auto;
}

.responsive-table {
    min-width: 100%;
}
</style>

